import React, { useEffect, useState } from 'react';
import {
	Box,
	Button,
	ButtonBase,
	Checkbox,
	Grid2,
	InputBase,
	Typography,
} from '@mui/material';
import { EyeIcon, HideEyeIcon } from '../assets/icons/Icons';
import { useNavigate } from 'react-router-dom';
import TextInput from '../components/TextInput';
import { helperText } from '../assets/theme/Theme';
import { createNewUserAccount } from '../services/UserService';
import { toast } from 'react-toastify';
import { MAIN_THEME_COLOR } from '../assets/theme/Colors';

const initialErrorState = {
	status: false,
	msg: '',
};

function CreateAccount() {
	const navigator = useNavigate();
	const [firstName, setFirstName] = useState('');
	const [lastName, setLastName] = useState('');
	const [email, setEmail] = useState('');
	const [password, setPassword] = useState('');
	const [reEnterPassword, setReEnterPassword] = useState('');
	const [firstNameError, setFirstNameError] = useState(initialErrorState);
	const [lastNameError, setLastNameError] = useState(initialErrorState);
	const [emailError, setEmailError] = useState(initialErrorState);
	const [passwordError, setPasswordError] = useState(initialErrorState);
	const [passwordReEnterError, setPasswordReEnterError] =
		useState(initialErrorState);
	const [show, setShow] = useState(false);
	const [showReEntered, setShowReEntered] = useState(false);
	const [isAgreed, setIsAgreed] = useState(false);
	const [termsError, setTermsError] = useState(initialErrorState);

	function onChangeFirstName(value: string) {
		setFirstNameError(initialErrorState);
		setFirstName(value);
	}

	function onChangeLastName(value: string) {
		setLastNameError(initialErrorState);
		setLastName(value);
	}

	function onChangeEmail(value: string) {
		setEmailError(initialErrorState);
		setEmail(value);
	}

	function onChangePassword(
		event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
	) {
		setPasswordError(initialErrorState);
		setPassword(event.target.value);
	}

	function onChangeReEnterPassword(
		event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
	) {
		setPasswordReEnterError(initialErrorState);
		setReEnterPassword(event.target.value);
	}

	function onValidateInputs() {
		let error = false;

		if (!firstName || firstName === '') {
			setFirstNameError({ status: true, msg: 'First Name is Required.' });
			error = true;
		}
		if (!lastName || lastName === '') {
			setLastNameError({ status: true, msg: 'Last Name is Required.' });
			error = true;
		}
		if (!email || email === '') {
			setEmailError({ status: true, msg: 'Email is Required.' });
			error = true;
		}
		if (email !== '' && email.indexOf('@') === -1) {
			setEmailError({ status: true, msg: 'Invalid Email' });
			error = true;
		}
		if (!password || password === '') {
			setPasswordError({ status: true, msg: 'Password is Required.' });
			error = true;
		}
		if (!reEnterPassword || reEnterPassword === '') {
			setPasswordReEnterError({
				status: true,
				msg: 'Re-enter the password.',
			});
			error = true;
		}
		if (password.length < 8) {
			setPasswordError({
				status: true,
				msg: 'Password should be at least 8 characters long.',
			});
			error = true;
		}
		if (password.length >= 8 && password !== reEnterPassword) {
			setPasswordReEnterError({
				status: true,
				msg: 'Password is not matched.',
			});
			error = true;
		}

		if (!isAgreed) {
			setTermsError({
				status: true,
				msg: 'Please agree to terms and condition',
			});
			error = true;
		}

		return error;
	}

	async function onClickCreateAccount() {
		const error = onValidateInputs();

		if (error) {
			return;
		} else {
			const body = {
				firstname: firstName,
				lastname: lastName,
				username: `${firstName} ${lastName}`,
				email: email,
				password: password,
			};
			const response = await createNewUserAccount(body);
			if (response.message === 'Success') {
				navigator('/');
				toast.success('Account created successfully.');
			} else {
				toast.error(response.message);
			}
		}
	}

	return (
		<div
			style={{
				display: 'flex',
				justifyContent: 'center',
				alignItems: 'center',
				minHeight: '100vh',
			}}>
			<Grid2
				container
				justifyContent={'center'}
				width={{ md: '50%', xs: '100%' }}>
				<Grid2 component={'div'}>
					<Typography
						variant="h6"
						fontWeight={'700'}
						color={MAIN_THEME_COLOR}
						paddingLeft="8px">
						{`Create Account`}
					</Typography>
				</Grid2>
				<Grid2
					component={'div'}
					width={'80%'}
					style={{
						textAlign: 'left',
						border: '1px solid #F4F5F6',
						boxShadow: '0px 10px 110px 1px rgba(59, 59, 59, 0.08)',
						marginTop: '20px',
						paddingTop: '20px',
						padding: '15px',
						borderRadius: '10px',
						backgroundColor: '#FFF',
					}}>
					<Grid2 container columnSpacing={1} paddingBottom="10px">
						<Grid2 component={'div'} size={{ xs: 12, md: 6 }}>
							<TextInput
								inputTitle={'First Name'}
								inputError={firstNameError}
								onChange={onChangeFirstName}
							/>
						</Grid2>
						<Grid2 component={'div'} size={{ xs: 12, md: 6 }}>
							<TextInput
								inputTitle={'Last Name'}
								inputError={lastNameError}
								onChange={onChangeLastName}
							/>
						</Grid2>
					</Grid2>
					<TextInput
						inputTitle={'Email'}
						inputError={emailError}
						onChange={onChangeEmail}
					/>
					<Typography
						fontSize={'16px'}
						color={'#7C7C7C'}
						paddingY="10px">
						Password
					</Typography>
					<Grid2
						container
						justifyContent={'space-between'}
						alignItems={'center'}
						border={
							passwordError.status
								? '1px solid red'
								: '1px solid #D9D9D9'
						}
						color={'#777E90'}
						paddingX={'15px'}
						borderRadius={'5px'}>
						<InputBase
							type={show === false ? 'password' : 'text'}
							sx={{
								padding: '4px',
								width: '90%',
								color: '#777E90',
							}}
							error={passwordError.status}
							onChange={onChangePassword}
						/>
						<ButtonBase onClick={() => setShow(!show)}>
							{show ? (
								<HideEyeIcon error={passwordError.status} />
							) : (
								<EyeIcon error={passwordError.status} />
							)}
						</ButtonBase>
					</Grid2>
					<Typography style={helperText}>
						{passwordError.msg}
					</Typography>
					<Typography
						fontSize={'16px'}
						color={'#7C7C7C'}
						paddingY="10px">
						Re-enter Password
					</Typography>
					<Grid2
						container
						justifyContent={'space-between'}
						alignItems={'center'}
						border={
							passwordReEnterError.status
								? '1px solid red'
								: '1px solid #D9D9D9'
						}
						color={'#777E90'}
						paddingX={'15px'}
						borderRadius={'5px'}>
						<InputBase
							type={showReEntered === false ? 'password' : 'text'}
							sx={{
								padding: '4px',
								width: '90%',
								color: '#777E90',
							}}
							error={passwordReEnterError.status}
							onChange={onChangeReEnterPassword}
						/>
						<ButtonBase
							onClick={() => setShowReEntered(!showReEntered)}>
							{showReEntered ? (
								<HideEyeIcon error={passwordError.status} />
							) : (
								<EyeIcon error={passwordError.status} />
							)}
						</ButtonBase>
					</Grid2>
					<Typography style={helperText}>
						{passwordReEnterError.msg}
					</Typography>
					<Grid2
						container
						alignItems={'center'}
						justifyContent={'flex-start'}
						spacing={0}
						style={{
							display: 'flex',
							alignItems: 'center',
							flexWrap: 'nowrap',
							width: '100%',
							marginTop: '10px',
						}}>
						<Checkbox
							style={{
								paddingLeft: '0px',
								paddingRight: '0px',
							}}
							size="small"
							color="success"
							disableRipple
							onChange={() => {
								setTermsError(initialErrorState);
								setIsAgreed(!isAgreed);
							}}
							checked={isAgreed}
						/>
						<Typography
							style={{
								display: 'inline',
								color: '#10292B',
								paddingLeft: '5px',
								fontSize: '12px',
								lineHeight: '18px',
							}}>
							I agree that my email may be used for{' '}
							<span style={{ fontWeight: 'bold' }}>
								marketing, product updates, and direct
								reach-outs to collect feedback.
							</span>
						</Typography>
					</Grid2>
					<Typography style={helperText}>{termsError.msg}</Typography>
					<Grid2 container justifyContent={'center'}>
						<Button
							onClick={onClickCreateAccount}
							style={{
								width: '100%',
								boxShadow: 'none',
								marginTop: '10px',
								textTransform: 'none',
								backgroundColor: '#28696D',
							}}
							variant="contained">
							Create Account
						</Button>
						<Typography
							variant="subtitle2"
							style={{
								color: '#777E91',
								paddingTop: '15px',
								paddingBottom: '5px',
							}}>
							<a href="/login" style={{ textDecoration: 'none' }}>
								<span
									style={{
										color: '#777E90',
										cursor: 'pointer',
									}}>
									Back to Login
								</span>
							</a>
						</Typography>
					</Grid2>
				</Grid2>
			</Grid2>
		</div>
	);
}

export default CreateAccount;
